import { ArrowUpRight } from "lucide-react";
import HoverLift from "@/components/motion/HoverLift";
import { StaggerItem, StaggerReveal } from "@/components/motion/StaggerReveal";

const TOGETHER = "https://posthog-cyan.vercel.app/illustrations/together.svg";
const LAUNCH = "https://posthog-cyan.vercel.app/illustrations/launch.svg";
const DEMO = "https://posthog-cyan.vercel.app/illustrations/resent.svg";

const PILLARS = [
    {
        title: "Build Together",
        number: "01",
        description:
            "We build in the same room, at the same time. Accountability on attaining our goals — not a Discord graveyard.",
        illo: TOGETHER,
        tone: "dark",
    },
    {
        title: "Roll Out Together",
        number: "02",
        description:
            "We deploy our projects to the community first — real users, real feedback, real iteration.",
        illo: LAUNCH,
        tone: "light",
    },
    {
        title: "Demonstrate",
        number: "03",
        description:
            "We demo our work to the co-working group to sharpen clarity and presentation craft.",
        illo: DEMO,
        tone: "accent",
    },
];

export default function Pillars() {
    return (
        <section
            id="pillars"
            data-testid="pillars-section"
            className="relative py-20 md:py-32"
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">
                            <span className="h-px w-8 bg-zinc-400" />
                            How we work
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.03em] text-zinc-950 leading-[0.95]">
                            Three habits. <br />
                            One community that actually ships.
                        </h2>
                    </div>
                    <p className="max-w-sm text-base text-zinc-600 leading-relaxed">
                        Most tech communities collect members. We collect
                        finished projects, deployed demos, and the receipts to
                        prove it.
                    </p>
                </div>

                {/* Three equal cards in one row on md+ */}
                <StaggerReveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 items-stretch">
                    {PILLARS.map((p) => (
                        <StaggerItem key={p.title}>
                            <HoverLift className="h-full">
                                <PillarCard
                                    {...p}
                                    testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                                />
                            </HoverLift>
                        </StaggerItem>
                    ))}
                </StaggerReveal>
            </div>
        </section>
    );
}

function PillarCard({ title, number, description, illo, tone, testid }) {
    const tones = {
        dark: "bg-zinc-950 text-white border-zinc-800 shadow-lg shadow-zinc-950/20",
        light:
            "bg-white text-zinc-950 border-black/[0.08] shadow-lg shadow-zinc-950/5",
        accent:
            "bg-[#E54D2E] text-white border-[#c73d24] shadow-lg shadow-[#E54D2E]/25",
    };
    const subText = {
        dark: "text-zinc-400",
        light: "text-zinc-600",
        accent: "text-white/85",
    };
    return (
        <article
            data-testid={testid}
            className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border motion-card ${tones[tone]}`}
        >
            <div className="flex min-h-[380px] flex-1 flex-col p-7 md:p-8">
                <div className="flex flex-1 flex-col">
                    <div
                        className={`flex items-center gap-3 font-mono text-xs tracking-[0.2em] mb-6 ${subText[tone]}`}
                    >
                        <span>{number}</span>
                        <span className="h-px w-8 bg-current opacity-30" />
                        <span className="uppercase">Pillar</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.65rem] font-bold tracking-tight leading-tight">
                        {title}
                    </h3>
                    <p
                        className={`mt-3 flex-1 text-sm md:text-base leading-relaxed ${subText[tone]}`}
                    >
                        {description}
                    </p>
                    <div
                        className={`mt-6 inline-flex items-center gap-2 text-sm font-medium ${subText[tone]}`}
                    >
                        <span className="h-8 w-8 shrink-0 rounded-full border border-current inline-flex items-center justify-center group-hover:bg-current group-hover:text-zinc-950 transition-colors">
                            <ArrowUpRight className="h-4 w-4" />
                        </span>
                        Learn the ritual
                    </div>
                </div>
                <div className="mt-6">
                    <div
                        className={`rounded-2xl p-4 ${
                            tone === "light"
                                ? "bg-[#FAFAFA]"
                                : "bg-white/5 backdrop-blur"
                        }`}
                    >
                        <img
                            src={illo}
                            alt={title}
                            className="motion-img mx-auto h-36 w-full max-w-[220px] object-contain md:h-40"
                            loading="lazy"
                        />
                    </div>
                </div>
            </div>
        </article>
    );
}
